import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../../assets/icons/activety-icon.svg';
import { ReactComponent as CloseCircleIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditPencil } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as GridList } from '../../../assets/icons/grid-list.svg';
import { ReactComponent as ActivePersonIcon } from '../../../assets/icons/person-icon-active.svg';
import { ReactComponent as UserIcon } from '../../../assets/icons/sidebar/user-icon.svg';
import { ReactComponent as TableList } from '../../../assets/icons/table-list.svg';
import { ReactComponent as DotCircle } from '../../../assets/icons/tables/dot-circle.svg';
import NoDataFound from '../../../assets/images/no-data-found.png';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import {
  RequestUserInterFace,
  UserInterFace,
  UserResponseInterFace,
} from '../../../interface/users-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disableUsersFilterApplied,
  fetchAllUsers,
  fetchUserActivityLogs,
  fetchUsersFilters,
  updateUsersFilters,
  setSortData,
} from '../../../redux/slices/UsersManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CardBox from '../../../shared-components/card-box/CardBox';
import CustomTableHeaderV2 from '../../../shared-components/custom-table/components/TableHeaderV2';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import Pagination from '../../../shared-components/pagination/Pagination';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { splitArrayIntoChunks } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import userApi from '../../../utils/api/users';

import ActiveLogCard from './active-log-card';
import AddUser from './add-user';
import DeleteUser from './delete-user';
import UserCardBody from './user-card-body';
import UserDetailsSidePanel from './UserDetailsSidePanel';

/**
 * User Management page function component
 */
function UsersList() {
  const branding = useAppBranding();
  const { t } = useTranslation('userRole');
  const dispatch = useAppDispatch();
  const {
    users,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.users);

  const [activeLogModal, setActiveLogModal] = useState(false);
  const [openTab, setOpenTab] = useState(0);
  const [position, setPosition] = useState<any>({});
  const [fullWidth, setFullWidth] = useState<any>({});
  const [modalView, setModalView] = useState({
    status: false,
    title: t('addAccount'),
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [isAddUserMode, setIsAddUserMode] = useState<boolean>(false);

  /**
   * Fetch Users list function
   */
  const fetchData = (data: RequestUserInterFace) => {
    if (!loading) {
      dispatch(fetchAllUsers(data));
    }
  };
  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: false,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: RequestUserInterFace) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({
      ...queryParameters,
      ...value,
      sortField,
      sortBy,
    });
  };

  /**
   * Completed the update task
   * @param message
   */
  const completedTask = (message: string) => {
    fetchData({
      limit: DefaultApiParameters.limit,
      page: DefaultApiParameters.page,
    });
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={closeModal}
        />
      ),
      rightCorner: '',
      notification: true,
    });
  };

  /**
   * The delete the user confirm function
   */
  const deleteConfirmFunction = (accountId: String) => {
    userApi
      .deleteUser(accountId)
      .then(() => {
        dispatch(
          fetchAllUsers({
            page,
            limit,
          }),
        );
        completedTask(t('userDeleted'));
      })
      .catch((error) => {
        closeModal();
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * User Delete function
   */
  const deleteFunction = (currentData: String) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      content: (
        <DeleteUser
          title={t('areYouSureDelete')}
          subTitle={t('deleteWarning')}
          closeModal={() => {
            closeModal();
          }}
          submitButton={
            <MyButton
              label={t('delete')}
              buttonType="error"
              onClickFunc={() => deleteConfirmFunction(currentData)}
              svgIcon={<DeleteIcon />}
            />
          }
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Edit the user function
   * @param currentDate
   */
  const editUser = (currentDate: String) => {
    setModalView({
      status: true,
      title: t('editUser'),
      header: true,
      content: (
        <AddUser
          closeModal={closeModal}
          currentDate={users.find((user) => user.id === currentDate)}
          completedTask={completedTask}
        />
      ),
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * User details view function
   */
  const viewFunction = (currentData: String, event?: React.MouseEvent) => {
    if (event) {
      setActiveLogModal(true);
      if (event.clientY > 320) {
        setPosition({ top: `${event.clientY - 320}px`, '--card-left-position': '5%' });
      } else {
        setPosition({
          top: `${event.clientY + 40}px`,
          '--card-bottom-position': '91%',
          '--card-left-position': '5%',
        });
      }
      dispatch(fetchUserActivityLogs(currentData));
    }
  };

  /**
   * User details view function
   */
  const viewCardActiveFunction = (
    currentData: String,
    userIndex: number,
    event?: React.MouseEvent,
  ) => {
    if (event) {
      let cardLeftPosition = '13%';
      let gridRowStart = 1;
      let rowCount = 4;
      const array = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      const array2 = [
        [0, 4, 8],
        [1, 5, 9],
        [2, 6],
        [3, 7],
      ];
      if (window.innerWidth > 1571) {
        rowCount = 4;
      } else if (window.innerWidth > 1333) {
        rowCount = 3;
      } else if (window.innerWidth > 840) {
        rowCount = 2;
      } else {
        rowCount = 1;
      }
      const chunks = splitArrayIntoChunks(array, rowCount);
      cardLeftPosition = `${(100 * userIndex) / rowCount / 2}%`;
      gridRowStart = chunks.findIndex((value) => value.includes(userIndex)) + 1;
      cardLeftPosition = `${(100 * gridRowStart) / rowCount / 2}%`;

      if (rowCount === 1) {
        cardLeftPosition = '50%';
      } else if (rowCount === 2) {
        cardLeftPosition = userIndex % 2 !== 0 ? '75%' : '25%';
      } else if (rowCount === 3) {
        if (userIndex && [0, 3, 6, 9].includes(userIndex)) {
          cardLeftPosition = '20%';
        } else if (userIndex && [1, 4, 7].includes(userIndex)) {
          cardLeftPosition = '50%';
        } else if (userIndex && [2, 5, 8].includes(userIndex)) {
          cardLeftPosition = '80%';
        }
      } else if (rowCount === 4) {
        if (userIndex && array2[0].includes(userIndex)) {
          cardLeftPosition = '13%';
        } else if (userIndex && array2[1].includes(userIndex)) {
          cardLeftPosition = '37.5%';
        } else if (userIndex && array2[2].includes(userIndex)) {
          cardLeftPosition = '63.5%';
        } else if (userIndex && array2[3].includes(userIndex)) {
          cardLeftPosition = '86%';
        }
      }
      setPosition({
        '--card-left-position': cardLeftPosition,
        top: '0px',
        left: '0',
        position: 'relative',
      });
      setActiveLogModal(true);
      setFullWidth({
        gridRowStart,
      });
      dispatch(fetchUserActivityLogs(currentData));
    }
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: t('userName'),
      accessorKey: 'name' as keyof UserInterFace,
      sortAllow: true,
    },
    // {
    //   label: 'User Id',
    //   accessorKey: 'accountId' as keyof UserInterFace,
    // },
    {
      label: t('role'),
      accessorKey: 'roleName' as keyof UserInterFace,
      sortAllow: true,
    },
    {
      label: t('contact'),
      accessorKey: 'email' as keyof UserInterFace,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof UserInterFace,
      position: 'center',
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: UserInterFace) =>
        currentRow.status ? (
          <div className="tag-success-label">
            <DotCircle /> <span>{t('active')}</span>
          </div>
        ) : (
          <div className="tag-danger-label">
            <DotCircle /> <span>{t('inactive')}</span>
          </div>
        ),
    },
  ];

  /**
   * Export data function
   */
  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('userName'), accessorKey: 'name' },
    { label: t('role'), accessorKey: 'roleName' },
    { label: t('contact'), accessorKey: 'email' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    userApi
      .exportUsers(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: UserResponseInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row.createdAt);
              case 'name':
                return row.name;
              case 'roleName':
                return row?.role ? row?.role?.name : row?.role;
              case 'email':
                return row.email;
              case 'status':
                return `${row.status ? t('active') : t('inactive')}`;
              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );

        if (type === 'csv') {
          exportCSV(headers, rows, 'users-list.csv');
        } else {
          exportPDF(headers, rows, 'users-list.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Function to open side panel on row click
   */
  const handleOpenSidePanelOnRowClick = (userId: string) => {
    setIsAddUserMode(false);
    searchParameters.set('userId', userId);
    setSearchParameters(searchParameters);
    if (openTab === 1) {
      setSidePanelOpen(true);
    }
  };
  /**
   * Add user function
   * @returns
   */
  const handleAddUser = () => {
    setIsAddUserMode(true);
    setSidePanelOpen(true);
  };
  useEffect(() => {
    if (isFilterUpdated || users.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableUsersFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('users'),
        icon: <UserIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-content">
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      {openTab === 0 && (
        <>
          <CustomTable
            header={column}
            data={users}
            serialNo
            version="v2"
            actionButton
            filters={filters}
            filtersToDispatch={updateUsersFilters}
            filterListLoading={filterListLoading}
            filterListToDispatch={fetchUsersFilters}
            sizeChangeButtonRequired
            height={window.innerHeight >= 700 ? 332 : 288}
            page={page}
            limit={limit}
            loading={loading}
            totalPages={totalPages}
            totalResults={totalResults}
            sortData={{ sortBy, sortField }}
            handleSortChange={handleSortFunction}
            pageLimitChange={pageNateLimitChange}
            refreshRequired
            handleRefresh={handleRefresh}
            hoverLabel="Refresh"
            ReferenceCurrentPages={handleRefresh}
            exportDataPdfOrCSV={exportDataPdfOrCSV}
            onHiddenColumnsChange={setHiddenColumns}
            exportLoader={exportLoader}
            actionButtonList={[
              {
                icon: <EditPencil />,
                function: editUser,
                color: 'view-color',
                allow: true,
              },
              {
                icon: <ActivityIcon />,
                function: viewFunction,
                color: 'view-color',
                allow: true,
              },
              {
                icon: <DeleteIcon />,
                function: deleteFunction,
                color: 'delete-color',
                allow: true,
              },
            ]}
            rowClickEvent={handleOpenSidePanelOnRowClick}
            rowClickParamsKey="userId"
            sidePanelEnabled={sidePanelOpen && isAddUserMode}
            sidePanelManualContent={
              <AddUser closeModal={() => setSidePanelOpen(false)} completedTask={completedTask} />
            }
            onSidePanelClose={() => setSidePanelOpen(false)}
            rowClickSidePanel={<UserDetailsSidePanel fetchIdFrom="queryParams" />}
            rightTopContent={
              <div className="table-top-button-list">
                <div className="table-button-item">
                  <MyButton
                    label=""
                    buttonType="primary-outline"
                    onClickFunc={() => setOpenTab(0)}
                    svgIcon={<TableList />}
                  />
                </div>
                <div className="table-button-item">
                  <MyButton
                    label=""
                    buttonType="back"
                    onClickFunc={() => setOpenTab(1)}
                    svgIcon={<GridList />}
                  />
                </div>
                <div>
                  <MyButton
                    label={t('addUser')}
                    buttonType="submit"
                    onClickFunc={handleAddUser}
                    svgIcon={<ActivePersonIcon />}
                  />
                </div>
              </div>
            }
          />
          {/* ActiveLogCard removed as it's replaced by side panel */}
        </>
      )}
      {openTab === 1 && (
        <div className="card-details">
          <CustomTableHeaderV2
            filters={filters}
            filtersToDispatch={updateUsersFilters}
            filterListToDispatch={fetchUsersFilters}
            filterListLoading={filterListLoading || false}
            sizeChangeFunction={() => {}}
            activeSearchFilter
            exportLoader={exportLoader}
            exportDataPdfOrCSV={exportDataPdfOrCSV}
            rightTopContent={
              <div className="table-top-button-list">
                <div className="table-button-item">
                  <MyButton
                    label=""
                    buttonType="back"
                    onClickFunc={() => setOpenTab(0)}
                    svgIcon={<TableList />}
                  />
                </div>
                <div className="table-button-item">
                  <MyButton
                    label=""
                    buttonType="primary-outline"
                    onClickFunc={() => setOpenTab(1)}
                    svgIcon={<GridList />}
                  />
                </div>
                <div>
                  <MyButton
                    label={t('addUser')}
                    buttonType="submit"
                    onClickFunc={handleAddUser}
                    svgIcon={<ActivePersonIcon />}
                  />
                </div>
              </div>
            }
            ReferenceCurrentPages={handleRefresh}
          />
          <div className="custom-table-with-side-panel">
            <div
              className="card-list"
              style={{
                height: 'calc(100vh - 215px)',
                width: sidePanelOpen ? '60%' : '100%',
                transition: 'width 0.2s ease-in-out',
                overflowY: 'auto',
              }}>
              {!loading && users.length === 0 && (
                <div className="table-no-data-wrap">
                  <img src={NoDataFound} alt="" className="no-data-found-image" />
                  <div className="no-data-found-details">
                    <h3>{t('oopsNothingHere')}</h3>
                    <p>{t('noDataMessage')}</p>
                  </div>
                </div>
              )}
              {loading && (
                <div className="table-no-data-wrap">
                  <BatteryLoader />
                </div>
              )}
              {activeLogModal && (
                <div className="fixed-grid" style={fullWidth}>
                  <ActiveLogCard
                    closeModal={() => setActiveLogModal(false)}
                    activeLogModal={activeLogModal}
                    position={position}
                  />
                </div>
              )}
              {!loading &&
                users.map((value: UserInterFace, index: number) => (
                  <div
                    role="presentation"
                    key={value.id}
                    onClick={() => handleOpenSidePanelOnRowClick(value.id)}
                    style={{ cursor: 'pointer' }}>
                    <CardBox>
                      <UserCardBody
                        userDetails={value}
                        activeLog={(userId, event) => viewCardActiveFunction(userId, index, event)}
                        activeLogActive
                        menuList={[
                          {
                            content: (
                              <div className="text-primary flex-row">
                                <EditPencil /> {t('edit')}
                              </div>
                            ),
                            function: editUser,
                            allow: true,
                          },
                          {
                            content: (
                              <div className="text-danger flex-row">
                                <DeleteIcon /> {t('delete')}
                              </div>
                            ),
                            function: deleteFunction,
                            allow: true,
                          },
                        ]}
                      />
                    </CardBox>
                  </div>
                ))}
            </div>
            {sidePanelOpen && (
              <div className="side-panel-container">
                <div>
                  <div
                    className="close-icon"
                    onClick={() => setSidePanelOpen(false)}
                    role="presentation">
                    <CloseCircleIcon />
                  </div>
                  {isAddUserMode ? (
                    <AddUser
                      closeModal={() => setSidePanelOpen(false)}
                      completedTask={completedTask}
                    />
                  ) : (
                    <UserDetailsSidePanel fetchIdFrom="queryParams" />
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="pagination">
            <div className="table-bottom-button-list">
              <div className="table-button-item" />
              <div className="table-button-item">
                <div className="user-count">
                  <div className="icon">
                    <branding.icons.MultiUser />
                  </div>
                  <div className="value">
                    {totalResults} {t('users')}
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              active={page}
              setActive={pageNateLimitChange}
              size={totalPages}
              totalResults={totalResults}
              limit={limit}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default UsersList;
